import { Box, CircularProgress } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import * as Sentry from '@sentry/react'
import { Amplify } from 'aws-amplify'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import React from 'react'
import { createRoot } from 'react-dom/client'
import TagManager from 'react-gtm-module'
import { BrowserRouter } from 'react-router-dom'
import SmartBanner from 'react-smartbanner'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'

import { AuthProvider } from '@app/context/auth'

import './i18n/config'

import App from './App'
import { ErrorPage } from './components/ErrorPage'
import { UpdateVersionDialog } from './components/UpdateVersionDialog'
import { SnackbarProvider } from './context/snackbar'
import theme from './theme'
import { AwsRegions } from './types'
import 'react-smartbanner/dist/main.css'

if (import.meta.env.VITE_APP_VERSION) {
  console.info(`App version is ${import.meta.env.VITE_APP_VERSION}`)
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.extraErrorDataIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACING_SAMPLE_RATE),
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  release: import.meta.env.VITE_APP_VERSION,
  denyUrls: [/localhost/i, /127.0.0.1/],
  replaysSessionSampleRate: Number(
    import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
  ),
  replaysOnErrorSampleRate: Number(
    import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
  ),
})

const isAnzLocalDev = window.location.host === 'localhost:4000'
// 👆👆 used to set up cognito for anz local development

// The `single-sign-on` flag decides which Cognito user pool (legacy vs. the
// new Managed Login/SSO pool) the whole app authenticates against for this
// page load, so it must be resolved before Amplify is configured. Switching
// the flag only takes effect on the next page refresh (see bootstrap below),
// which is intentional - no dynamic mid-session pool switching.
function waitForFeatureFlags(timeoutMs = 3000): Promise<void> {
  return new Promise(resolve => {
    const timeout = setTimeout(resolve, timeoutMs)
    posthog.onFeatureFlags(() => {
      clearTimeout(timeout)
      resolve()
    })
  })
}

function configureAmplify(managedLoginEnabled: boolean) {
  if (managedLoginEnabled) {
    Amplify.configure({
      Auth: {
        region: import.meta.env.VITE_AWS_REGION,
        userPoolId: import.meta.env.VITE_COGNITO_SSO_USER_POOL_ID,
        userPoolWebClientId: import.meta.env
          .VITE_COGNITO_SSO_USER_POOL_CLIENT_ID,
        oauth: {
          domain: import.meta.env.VITE_COGNITO_SSO_DOMAIN,
          scope: ['email', 'openid', 'profile'],
          redirectSignIn: import.meta.env.VITE_COGNITO_SSO_REDIRECT_SIGNIN,
          redirectSignOut: import.meta.env.VITE_COGNITO_SSO_REDIRECT_SIGNOUT,
          responseType: 'code' as const,
        },
      },
    })
    return
  }

  if (isAnzLocalDev) {
    Amplify.configure({
      Auth: {
        region: import.meta.env.VITE_AWS_REGION,
        userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID_ANZ,
        userPoolWebClientId: import.meta.env
          .VITE_COGNITO_USER_POOL_CLIENT_ID_ANZ,
      },
    })
  } else {
    Amplify.configure({
      Auth: {
        region: import.meta.env.VITE_AWS_REGION,
        userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
        userPoolWebClientId: import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID,
      },
    })
  }
}

TagManager.initialize({ gtmId: import.meta.env.VITE_GTM_ID })

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(document.getElementById('app')!)
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const updateBanner = createRoot(document.getElementById('update-banner')!)
const smartAppBanner = createRoot(
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  document.getElementById('smart-app-banner')!,
)

const posthogApiKey =
  import.meta.env.VITE_AWS_REGION === AwsRegions.Australia
    ? import.meta.env.VITE_PUBLIC_POSTHOG_KEY_AU
    : import.meta.env.VITE_PUBLIC_POSTHOG_KEY

posthog.init(posthogApiKey, {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
})

// Overrides take precedence over remote flags, so hooks like useFeatureFlagEnabled also see `false`.
if (import.meta.env.VITE_E2E_FORCE_LEGACY_AUTH === 'true') {
  posthog.featureFlags.overrideFeatureFlags({
    flags: { 'single-sign-on': false },
  })
}

const currentVersion = localStorage.getItem('appVersion')

if (!currentVersion || currentVersion === 'undefined') {
  localStorage.setItem('appVersion', '0.0.0')
}

updateBanner.render(
  <React.StrictMode>
    <SnackbarProvider>
      <ThemeProvider theme={theme}>
        <UpdateVersionDialog />
      </ThemeProvider>
    </SnackbarProvider>
  </React.StrictMode>,
)

const SmartBannerWrapper = () => {
  return (
    <SmartBanner daysHidden={7} title="Team Teach Connect" position="top" />
  )
}

smartAppBanner.render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <SnackbarProvider>
        <ThemeProvider theme={theme}>
          <SmartBannerWrapper />
        </ThemeProvider>
      </SnackbarProvider>
    </PostHogProvider>
  </React.StrictMode>,
)

root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    </ThemeProvider>
  </React.StrictMode>,
)

void waitForFeatureFlags().then(() => {
  // Force legacy auth for E2E tests. This is a temporary measure to make E2E tests pass in PRs
  // In the near future, we should address this, write new e2e tests for the new SSO flow, and remove this code.
  const e2eForceingLegacy =
    import.meta.env.VITE_E2E_FORCE_LEGACY_AUTH === 'true'
  const managedLoginEnabled =
    !e2eForceingLegacy && (posthog.isFeatureEnabled('single-sign-on') ?? false)
  configureAmplify(managedLoginEnabled)

  root.render(
    <React.StrictMode>
      <PostHogProvider client={posthog}>
        <ThemeProvider theme={theme}>
          <BrowserRouter>
            <QueryParamProvider
              adapter={ReactRouter6Adapter}
              options={{ updateType: 'replaceIn' }}
            >
              <Sentry.ErrorBoundary
                fallback={errorData => (
                  <ErrorPage
                    errorData={errorData}
                    debug={
                      import.meta.env.VITE_SENTRY_ENVIRONMENT === 'development'
                    }
                  />
                )}
              >
                <AuthProvider>
                  <App />
                </AuthProvider>
              </Sentry.ErrorBoundary>
            </QueryParamProvider>
          </BrowserRouter>
        </ThemeProvider>
      </PostHogProvider>
    </React.StrictMode>,
  )
})
